import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard, Fingerprint, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { Avatar, Cargando, Flotante, botonAzul, botonClaro, campo, display } from '../asistencia/ui';
import Manos from './Manos';
import Huella from './Huella';
import { Interruptor, Ventana } from './ui';
import { fecha, nombrePuerta } from './comun';

const botonPeligro = 'h-9 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 border border-rose-200 text-rose-700 bg-white hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 disabled:opacity-40';
const botonIcono = 'w-9 h-9 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 disabled:opacity-40';

const Seccion = ({ titulo, icono: Icono, children }) => (
    <section className="space-y-3">
        <h3 className={`${display} text-sm font-bold text-slate-900 inline-flex items-center gap-2`}>
            <Icono size={15} className="text-sura-blue" /> {titulo}
        </h3>
        {children}
    </section>
);

const Tarjeta = ({ tarjeta, grupos, puedeGestionar, onCambio }) => {
    const [ocupada, setOcupada] = useState(false);
    const [borrar, setBorrar] = useState(false);

    const guardar = async (cambios, mensaje) => {
        setOcupada(true);
        try {
            await axiosInstance.put(`/api/puertas/tarjetas/${tarjeta.id}`, cambios);
            toast.success(mensaje);
            onCambio();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cambiar la tarjeta');
        } finally {
            setOcupada(false);
        }
    };

    const quitar = async () => {
        setOcupada(true);
        try {
            await axiosInstance.delete(`/api/puertas/tarjetas/${tarjeta.id}`);
            toast.success(`Tarjeta ${tarjeta.numero} borrada`);
            setBorrar(false);
            onCambio();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo borrar la tarjeta');
            setOcupada(false);
        }
    };

    return (
        <li className="flex flex-wrap items-center gap-3 py-3 border-t border-slate-100 first:border-t-0">
            <span className="min-w-[7.5rem]">
                <span className={`block tabular-nums ${tarjeta.activa ? 'text-slate-900 font-medium' : 'text-slate-400 line-through'}`}>{tarjeta.numero}</span>
                <span className="block text-xs text-slate-400">
                    {tarjeta.activa && !tarjeta.abre ? 'Su grupo no abre ninguna puerta' : tarjeta.ultimo_acceso ? `Último uso ${fecha(tarjeta.ultimo_acceso)}` : 'Sin uso registrado'}
                </span>
            </span>
            <label className="flex-1 min-w-[9rem]">
                <span className="sr-only">Grupo de acceso</span>
                <select
                    value={tarjeta.grupo_id ?? ''} disabled={!puedeGestionar || ocupada}
                    onChange={(e) => guardar({ grupo_id: Number(e.target.value) }, `Tarjeta ${tarjeta.numero}: ahora en ${grupos.find((g) => g.id === Number(e.target.value))?.nombre}`)}
                    className={`${campo} w-full pr-8`}
                >
                    {tarjeta.grupo_id == null && <option value="">{tarjeta.grupo || 'Sin grupo'}</option>}
                    {grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                </select>
            </label>
            <span className="inline-flex items-center gap-2">
                <span className={`text-xs ${tarjeta.activa ? 'text-sura-blue' : 'text-slate-400'}`}>{tarjeta.activa ? 'Activa' : 'Inactiva'}</span>
                <Interruptor
                    activo={tarjeta.activa} disabled={!puedeGestionar || ocupada} etiqueta={`Tarjeta ${tarjeta.numero} activa`}
                    onCambio={(activa) => guardar({ activa }, `Tarjeta ${tarjeta.numero} ${activa ? 'activada' : 'desactivada'}`)}
                />
            </span>
            {puedeGestionar && (
                <button type="button" onClick={() => setBorrar(true)} disabled={ocupada} className={botonIcono} aria-label={`Borrar la tarjeta ${tarjeta.numero}`}>
                    <Trash2 size={16} />
                </button>
            )}
            {borrar && (
                <Ventana titulo={`¿Borrar la tarjeta ${tarjeta.numero}?`} onCerrar={() => !ocupada && setBorrar(false)}>
                    <p className="text-sm mt-2 text-slate-500">Deja de abrir de inmediato. Las huellas amarradas a esta tarjeta también dejan de abrir.</p>
                    <div className="flex justify-end gap-2 mt-6">
                        <button type="button" onClick={() => setBorrar(false)} disabled={ocupada} className={botonClaro}>Cancelar</button>
                        <button type="button" onClick={quitar} disabled={ocupada} className={botonPeligro}>Borrar tarjeta</button>
                    </div>
                </Ventana>
            )}
        </li>
    );
};

// Darle una tarjeta: no hay tarjetas físicas, es el número al que se amarra la huella.
// Se le asigna una libre con el grupo de acceso elegido.
const DarTarjeta = ({ personaId, grupos, onLista, onCancelar }) => {
    const [grupo, setGrupo] = useState('');
    const [enviando, setEnviando] = useState(false);

    const dar = async (e) => {
        e.preventDefault();
        setEnviando(true);
        try {
            const { data } = await axiosInstance.post(`/api/puertas/personas/${personaId}/tarjetas`, { grupo_id: Number(grupo) });
            toast.success(`Tarjeta ${data.numero} asignada`);
            onLista();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo dar la tarjeta');
        } finally {
            setEnviando(false);
        }
    };

    const abren = grupos.filter((g) => g.abre);
    return (
        <form onSubmit={dar} className="flex flex-wrap items-end gap-2 pt-1">
            <label className="flex-1 min-w-[12rem]">
                <span className="text-xs text-slate-500">Qué puede abrir</span>
                <select value={grupo} onChange={(e) => setGrupo(e.target.value)} className={`${campo} w-full mt-1 pr-8`} required>
                    <option value="" disabled>Elige su grupo de acceso</option>
                    {(abren.length ? abren : grupos).map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                </select>
            </label>
            {onCancelar && <button type="button" onClick={onCancelar} disabled={enviando} className={botonClaro}>Cancelar</button>}
            <button type="submit" disabled={enviando || grupo === ''} className={botonAzul}>
                {enviando ? 'Asignando' : 'Darle una tarjeta'}
            </button>
        </form>
    );
};

// Lo que se hace con el dedo elegido: enrolarlo en un lector o borrarlo
const PanelDedo = ({ persona, dedo, lectores, puedeGestionar, onEnrolar, onBorrado }) => {
    const [lector, setLector] = useState(() => {
        try { return localStorage.getItem('puertas-lector') || ''; } catch (e) { return ''; }
    });
    const [tarjeta, setTarjeta] = useState(persona.tarjetas[0]?.numero || '');
    const [borrando, setBorrando] = useState(false);
    const d = persona.dedos[dedo];

    const borrar = async () => {
        setBorrando(true);
        try {
            await axiosInstance.delete(`/api/puertas/personas/${persona.id}/huellas/${dedo}`);
            toast.success(`Huella borrada: ${d.nombre.toLowerCase()}`);
            onBorrado();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo borrar la huella');
        } finally {
            setBorrando(false);
        }
    };

    const elegirLector = (id) => {
        setLector(id);
        try { localStorage.setItem('puertas-lector', id); } catch (e) { /* sin almacenamiento local */ }
    };

    return (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3 puertas-aparece">
            <p className="text-sm text-slate-700">
                <span className="font-medium text-slate-900">{d.nombre}</span>
                {d.estado === 'enrolado' ? `: huella guardada${d.tarjeta ? `, abre como la tarjeta ${d.tarjeta}` : ''}.` : d.estado === 'pendiente' ? ': pendiente de enrolar en el lector.' : ': sin huella.'}
            </p>
            {puedeGestionar && (
                persona.tarjetas.length === 0 ? (
                    <p className="text-sm text-slate-500">Primero dale una tarjeta: la huella va amarrada a una tarjeta.</p>
                ) : (
                    <>
                        <div className="grid sm:grid-cols-2 gap-2">
                            <label>
                                <span className="text-xs text-slate-500">Lector donde va a poner el dedo</span>
                                <select value={lector} onChange={(e) => elegirLector(e.target.value)} className={`${campo} w-full mt-1 pr-8`}>
                                    <option value="" disabled>Elige un lector</option>
                                    {lectores.map((l) => <option key={l.id} value={l.id}>{nombrePuerta(l.puerta)} ({l.puerta}), {l.lado}</option>)}
                                </select>
                            </label>
                            {persona.tarjetas.length > 1 && (
                                <label>
                                    <span className="text-xs text-slate-500">Abre como la tarjeta</span>
                                    <select value={tarjeta} onChange={(e) => setTarjeta(e.target.value)} className={`${campo} w-full mt-1 pr-8`}>
                                        {persona.tarjetas.map((t) => <option key={t.id} value={t.numero}>{t.numero}</option>)}
                                    </select>
                                </label>
                            )}
                        </div>
                        <div className="flex flex-wrap justify-end gap-2">
                            {d.estado !== 'vacio' && (
                                <button type="button" onClick={borrar} disabled={borrando} className={botonPeligro}>Borrar huella</button>
                            )}
                            <button
                                type="button" disabled={!lectores.some((l) => l.id === lector)}
                                onClick={() => onEnrolar(dedo, lectores.find((l) => l.id === lector), tarjeta)}
                                className={botonAzul}
                            >
                                <Fingerprint size={16} /> {d.estado === 'enrolado' ? 'Volver a enrolar' : 'Enrolar'}
                            </button>
                        </div>
                    </>
                )
            )}
        </div>
    );
};

/**
 * Ficha de una persona en las puertas: sus tarjetas (a qué grupo de acceso entran y si
 * están activas) y sus huellas, dedo por dedo. Se abre de lado, sobre la lista.
 */
const FichaPersona = ({ personaId, puedeGestionar, onCerrar, onCambio }) => {
    const [persona, setPersona] = useState(null);
    const [error, setError] = useState(null);
    const [lectores, setLectores] = useState([]);
    const [dedo, setDedo] = useState(null);
    const [enrolando, setEnrolando] = useState(null);   // { trabajoId, dedo, lector, tarjeta }
    const [quitar, setQuitar] = useState(false);
    const [quitando, setQuitando] = useState(false);
    const [otraTarjeta, setOtraTarjeta] = useState(false);

    const cargar = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get(`/api/puertas/personas/${personaId}`);
            setPersona(data);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo cargar la persona');
        }
    }, [personaId]);

    useEffect(() => { cargar(); }, [cargar]);
    useEffect(() => {
        axiosInstance.get('/api/puertas/lectores').then(({ data }) => setLectores(data.lectores)).catch(() => setLectores([]));
    }, []);
    useEffect(() => {
        const tecla = (e) => e.key === 'Escape' && !enrolando && !quitar && onCerrar();
        window.addEventListener('keydown', tecla);
        return () => window.removeEventListener('keydown', tecla);
    }, [enrolando, quitar, onCerrar]);

    const cambio = useCallback(() => { cargar(); onCambio(); }, [cargar, onCambio]);

    const enrolar = async (numeroDedo, lector, tarjeta) => {
        try {
            const { data } = await axiosInstance.post(`/api/puertas/personas/${personaId}/huellas`, {
                dedo: numeroDedo, lector_id: lector.id, tarjeta,
            });
            setEnrolando({ trabajoId: data.trabajo_id, dedo: numeroDedo, lector, tarjeta });
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo arrancar el enrolamiento');
        }
    };

    const quitarAcceso = async () => {
        setQuitando(true);
        try {
            await axiosInstance.delete(`/api/puertas/personas/${personaId}`);
            toast.success(`${persona.nombre} ya no está en las puertas`);
            onCambio();
            onCerrar();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo quitar el acceso');
            setQuitando(false);
        }
    };

    const enroladas = persona ? persona.dedos.filter((d) => d.estado === 'enrolado').length : 0;

    return (
        <Flotante>
            <div className="fixed inset-0 z-[80] flex justify-end">
                <div className="absolute inset-0 bg-slate-900/30" onClick={() => !enrolando && onCerrar()} />
                <aside
                    role="dialog" aria-modal="true" aria-label={persona ? `Ficha de ${persona.nombre}` : 'Ficha de la persona'}
                    className="relative h-full w-full max-w-[34rem] bg-white shadow-2xl flex flex-col puertas-desliza"
                >
                    <header className="px-6 pt-6 pb-5 flex items-start gap-4 border-b border-slate-100">
                        {persona ? <Avatar persona={{ nombre: persona.nombre, foto: persona.empleado?.foto }} size={56} /> : <span className="w-14 h-14" />}
                        <div className="flex-1 min-w-0">
                            <h2 className={`${display} text-xl font-bold text-slate-900 truncate`}>{persona?.empleado?.nombre || persona?.nombre || 'Persona'}</h2>
                            {persona && (
                                <p className="text-sm mt-0.5 text-slate-500">
                                    {[persona.cedula && `Cédula ${persona.cedula}`, persona.empleado?.departamento || persona.departamento].filter(Boolean).join(', ')}
                                </p>
                            )}
                            {persona?.empleado && !persona.empleado.activo && (
                                <p className="text-sm mt-1 text-amber-700 font-medium">Egresó de SURA y todavía puede entrar.</p>
                            )}
                            {persona && !persona.empleado && <p className="text-xs mt-1 text-slate-400">Su cédula no está en Centro SURA.</p>}
                            {persona?.empleado && ['ficha', 'nombre'].includes(persona.coincide) && (
                                <p className="text-xs mt-1 text-slate-400">
                                    En GeoVision su cédula está mal cargada o falta: la encontramos por su {persona.coincide}.
                                </p>
                            )}
                        </div>
                        <button type="button" onClick={onCerrar} className={botonIcono} aria-label="Cerrar la ficha"><X size={18} /></button>
                    </header>

                    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
                        {error && <p className="text-sm text-rose-600">{error}</p>}
                        {!persona && !error && <Cargando />}
                        {persona && (
                            <>
                                <Seccion titulo="Tarjetas" icono={CreditCard}>
                                    {persona.tarjetas.length === 0 ? (
                                        <p className="text-sm text-slate-500">No tiene tarjeta: no abre nada, y la huella se amarra a una tarjeta.</p>
                                    ) : (
                                        <ul>
                                            {persona.tarjetas.map((t) => (
                                                <Tarjeta key={t.id} tarjeta={t} grupos={persona.grupos} puedeGestionar={puedeGestionar} onCambio={cambio} />
                                            ))}
                                        </ul>
                                    )}
                                    {puedeGestionar && (persona.tarjetas.length === 0 || otraTarjeta ? (
                                        <DarTarjeta
                                            personaId={persona.id} grupos={persona.grupos}
                                            onLista={() => { setOtraTarjeta(false); cambio(); }}
                                            onCancelar={persona.tarjetas.length ? () => setOtraTarjeta(false) : null}
                                        />
                                    ) : (
                                        <button
                                            type="button" onClick={() => setOtraTarjeta(true)}
                                            className="text-sm text-sura-blue hover:underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                                        >
                                            Darle otra tarjeta
                                        </button>
                                    ))}
                                </Seccion>

                                <Seccion titulo="Huellas" icono={Fingerprint}>
                                    <p className="text-sm text-slate-500">
                                        {enroladas === 0 ? 'No tiene huellas guardadas.' : `${enroladas} de 10 dedos guardados.`}
                                        {puedeGestionar && ' Toca un dedo para enrolarlo o borrarlo.'}
                                    </p>
                                    <Manos dedos={persona.dedos} elegido={dedo} onElegir={(d) => setDedo(dedo === d ? null : d)} />
                                    {dedo != null && (
                                        <PanelDedo
                                            key={dedo} persona={persona} dedo={dedo} lectores={lectores} puedeGestionar={puedeGestionar}
                                            onEnrolar={enrolar} onBorrado={cambio}
                                        />
                                    )}
                                </Seccion>

                                {puedeGestionar && (
                                    <section className="pt-5 border-t border-slate-100">
                                        <p className="text-sm text-slate-500">Quitarla de las puertas la borra de GeoVision junto con sus tarjetas.</p>
                                        <button type="button" onClick={() => setQuitar(true)} className={`${botonPeligro} mt-3`}>Quitar de las puertas</button>
                                    </section>
                                )}
                            </>
                        )}
                    </div>
                </aside>

                {enrolando && persona && (
                    <Huella
                        trabajoId={enrolando.trabajoId} dedo={persona.dedos[enrolando.dedo].nombre}
                        lector={{ ...enrolando.lector, puerta: nombrePuerta(enrolando.lector.puerta) }}
                        onLista={cambio}
                        onCerrar={() => setEnrolando(null)}
                        onReintentar={() => { const e = enrolando; setEnrolando(null); enrolar(e.dedo, e.lector, e.tarjeta); }}
                    />
                )}
                {quitar && persona && (
                    <Ventana titulo={`¿Quitar a ${persona.empleado?.nombre || persona.nombre} de las puertas?`} onCerrar={() => !quitando && setQuitar(false)}>
                        <p className="text-sm mt-2 text-slate-500">No va a poder entrar por ninguna puerta.</p>
                        <div className="flex justify-end gap-2 mt-6">
                            <button type="button" onClick={() => setQuitar(false)} disabled={quitando} className={botonClaro}>Cancelar</button>
                            <button type="button" onClick={quitarAcceso} disabled={quitando} className={botonPeligro}>Quitar de las puertas</button>
                        </div>
                    </Ventana>
                )}
            </div>
        </Flotante>
    );
};

export default FichaPersona;
